import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useGlobalCounter = (counterName = 'layer_recommendations') => {
  const [count, setCount] = useState(null); // Start with null to avoid showing fallback
  const [isLoading, setIsLoading] = useState(true);
  const lastIncrementTime = useRef(0);
  const channelRef = useRef(null);

  // Fetch initial count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        console.log('Fetching counter for name:', counterName);
        console.log('Supabase client:', supabase);
        
        // First test basic connectivity
        const { data: testData, error: testError } = await supabase
          .from('global_counters')
          .select('*');
        console.log('All counters test:', { testData, testError });
        
        const { data, error } = await supabase
          .from('global_counters')
          .select('count')
          .eq('name', counterName)
          .single();

        console.log('Counter fetch result:', { data, error });
        if (error) throw error;
        setCount(data.count);
        console.log('Counter set to:', data.count);
      } catch (error) {
        console.error('Error fetching counter:', error);
        // Use fallback value only on error
        console.log('Using fallback value: 95154');
        setCount(95154);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCount();
  }, [counterName]);

  // Set up real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('global-counters')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'global_counters',
          filter: `name=eq.${counterName}`
        },
        (payload) => {
          setCount(payload.new.count);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [counterName]);

  const increment = async () => {
    // Debounce rapid calls (max 1 per second)
    const now = Date.now();
    if (now - lastIncrementTime.current < 1000) {
      return;
    }
    lastIncrementTime.current = now;

    try {
      const { data, error } = await supabase.rpc('increment_counter', {
        counter_name: counterName
      });

      if (error) throw error;
      // Count will be updated via real-time subscription
    } catch (error) {
      console.error('Error incrementing counter:', error);
      // Optimistically update on error
      setCount(prev => prev + 1);
    }
  };

  return { count, increment, isLoading };
};
